/* eslint-disable @typescript-eslint/naming-convention */
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import {
    BehaviorSubject,
    lastValueFrom,
    Observable,
    share,
    tap,
} from 'rxjs';
import {
    Directory,
    Encoding,
    Filesystem,
    ReadFileResult,
} from '@capacitor/filesystem';

import {
    apiKey,
    componentsDirectory,
    Config,
    dataDirectory,
    globalFallbackRegLang,
    meaDefaultRegion,
    NavItem,
    regionMap,
    SiteContent,
    TranslationData,
} from '../models';
import { NotificationService } from './notification.service';
import { environment } from '../../environments/environment';
import { PlatformService } from './platform.service';
import { getNestedComponentData } from '../data/component-data';

@Injectable({
    providedIn: 'root',
})
export class DataService {
    cache: Record<string, any> = {};
    region!: string;
    country!: string;
    config!: Config;
    fallbackLanguages!: string[];

    translationsData!: TranslationData;
    translationsData$: BehaviorSubject<any> = new BehaviorSubject({});

    readonly awsS3BucketUrl = `${dataDirectory.external}/${environment.bapiEnv}`;

    allData: SiteContent[] = [] as SiteContent[];
    navData: NavItem[] = [] as NavItem[];

    private httpClient = inject(HttpClient);
    private notificationService = inject(NotificationService);
    private platformService = inject(PlatformService);

    private bapiUrl = environment.apiUrl;

    getNavData(): SiteContent[] {
        return this.navData;
    }

    setAllData(data: { content: SiteContent[]; navigation: NavItem[] }) {
        this.allData = data.content;
        this.navData = data.navigation;
    }

    async initSiteWideData(region: string): Promise<void> {
        this.region = region;

        const configResponse = await this.loadTranslationConfig();
        this.setConfigProperties(configResponse);

        await Promise.all([
            this.loadTranslationsData(),
            this.loadComponentData(),
        ]);
    }

    private async loadTranslationConfig(): Promise<Record<string, Config>> {
        try {
            return await this.getBapiDataWithFallback<Record<string, Config>>(
                'config/sites'
            );
        } catch (e) {
            this.notificationService.error(
                (e as Error).message,
                'Could not fetch config.'
            );
            return {};
        }
    }

    getBapiData(
        path: string,
        options: Record<string, string | number> = {}
    ): Observable<Record<string, Config>> {
        let params = new HttpParams();

        Object.entries(options).forEach(([key, value]) => {
            params = params.set(key, String(value));
        });

        return this.httpClient.get<Record<string, Config>>(
            `${this.bapiUrl}/matrix-experience-app/${path}`,
            {
                headers: { 'x-api-key': apiKey },
                params,
            }
        );
    }

    /**
     * Fetch data from the bapi endpoint, falling back to the locally cached
     * copy (written by DataUpdater) when the network request fails, e.g. when
     * the device is offline.
     */
    private async getBapiDataWithFallback<T>(path: string, options?: Record<string, string | number>): Promise<any> {
        try {

            return await lastValueFrom(this.getBapiData(path, options));
        } catch (e) {
            if (this.platformService.isNativeDevice) {
                const read: ReadFileResult = await Filesystem.readFile({
                    path: `${dataDirectory.local}/${path}.json`,
                    directory: Directory.Data,
                    encoding: Encoding.UTF8,
                });
                return JSON.parse(read.data.toString());
            }
            throw e;
        }
    }

    setConfigProperties(configResponse: Record<string, Config>): void {
        this.config = configResponse[this.region];

        this.country = this.config.country;
        const defaultLanguage = this.config.defaultPreferredLanguage;
        this.fallbackLanguages =
            this.config.languages[defaultLanguage].fallback;
    }

    async loadComponentDataByName(componentName: string): Promise<any> {
        return this.getFileData(componentName);
    }

    async loadComponentData(): Promise<string | undefined> {
        const componentsDataFilePath = `${componentsDirectory}/component-data-content`;
        const componentConfigDataFilePath = `${componentsDirectory}/config`;

        try {
            const componentConfigData = await this.getFileData(
                componentConfigDataFilePath
            );
            const data = await this.getFileData(
                componentsDataFilePath + '-' + this.region
            );
            if (this.fallbackLanguages.includes(globalFallbackRegLang)) {
                const fallbackData = await this.getFileData(
                    `${componentsDataFilePath}-${meaDefaultRegion}`
                );
                const componentList: string[] = Object.keys(data);
                componentList.forEach((componentName) => {
                    if (!data[componentName].length) {
                        data[componentName] = fallbackData[componentName];
                    }
                });
            }

            const nestedComponentData = getNestedComponentData(
                data,
                componentConfigData
            );

            this.setAllData(nestedComponentData);

            return 'Page Data Loaded';
        } catch (e) {
            this.notificationService.error(
                (e as Error).message,
                'Could not fetch ' + componentsDataFilePath + '.json'
            );
        }
    }

    private async combineFallbacks(): Promise<any> {
        const translationsByRegion = await Promise.all(
            this.fallbackLanguages.map(async (regLang) => {
                const [lang, reg] = regLang.split('-');
                const translationData = await this.getBapiDataWithFallback<
                    any[]
                >(`translations/${regionMap(reg)}/${lang}`,{amount: 0});
                const preparedData = (translationData as any[]).reduce(
                    (keyedTranslationData, item) => {
                        keyedTranslationData[item.key] = {
                            key: item.key,
                            value: item.value,
                            is_markdown: item.is_markdown,
                        };
                        return keyedTranslationData;
                    },
                    {} as TranslationData
                );

                return { [`${lang}-${reg.toUpperCase()}`]: preparedData };
            })
        );

        return translationsByRegion.reduce(
            (merged, regionData) => ({ ...merged, ...regionData }),
            {} as TranslationData
        );
    }

    async loadTranslationsData(): Promise<any> {
        try {
            this.translationsData = await this.combineFallbacks();
            this.translationsData$.next(this.translationsData);
            return 'Translation Data Loaded';
        } catch (e) {
            this.notificationService.error(
                (e as Error).message,
                'Could not fetch translations.'
            );
        }
    }

    private async getFileData(fileName: string): Promise<any> {
        try {
            if (this.cache[fileName as keyof Record<string, any>]) {
                return Promise.resolve(
                    this.cache[fileName as keyof Record<string, any>]
                );
            }
            const noCacheHeaders = {
                'Cache-Control':
                    'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
                Expires: '0',
            };
            return lastValueFrom(
                this.httpClient
                    .get(`${this.awsS3BucketUrl}/${fileName}.json`, {
                        headers: noCacheHeaders,
                    })
                    .pipe(
                        share(),
                        tap(
                            (data) =>
                                (this.cache[
                                    fileName as keyof Record<string, any>
                                ] = data)
                        )
                    )
            );
        } catch (e) {
            if (this.platformService.isNativeDevice) {
                const read: ReadFileResult = await Filesystem.readFile({
                    path: `assets/data/${fileName}.json`,
                    directory: Directory.Data,
                    encoding: Encoding.UTF8,
                });
                return JSON.parse(read.data.toString());
            }
        }
    }
}
